
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, AlertTriangle, CheckCircle } from 'lucide-react';
import { db } from '@/firebase/config';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { calculateFeeBalances, PaymentAllocations, getApplicableTermFees, Term } from '@/utils/feeCalculations';

interface ClassFinanceDetailsProps {
    className: string;
    schoolID: string;
    onBack: () => void;
    paymentProviders: string[];
}

interface StudentFinanceRecord {
    id: string;
    name: string;
    regNo: string;
    grade: string;
    feesRequired: number;
    amountPaid: number;
    outstandingBalance: number;
    hasMissingData: boolean;
    status: 'paid' | 'overdue' | 'missing_data';
}

const ClassFinanceDetails: React.FC<ClassFinanceDetailsProps> = ({ className, schoolID, onBack, paymentProviders }) => {
    const [students, setStudents] = useState<StudentFinanceRecord[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchClassData = async () => {
            setLoading(true);
            try {
                // 1. Fetch Fee Structure AND Terms
                const feeDocRef = doc(db, `schools/${schoolID}/deployedFeeStructures/current`);
                const termsRef = collection(db, `schools/${schoolID}/terms`);
                
                const [feeDoc, termsSnap] = await Promise.all([
                    getDoc(feeDocRef),
                    getDocs(termsRef)
                ]);

                const feeData = feeDoc.exists() ? feeDoc.data() : null;
                const levelFees = feeData?.levelFees || [];
                const terms = termsSnap.docs.map(t => ({ id: t.id, ...t.data() } as Term));

                // 2. Fetch Students in Class
                let studentsList: any[] = [];
                const q1 = query(collection(db, `schools/${schoolID}/students`), where("class", "==", className));
                const q2 = query(collection(db, `schools/${schoolID}/students`), where("className", "==", className));
                
                const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
                const studentMap = new Map();
                snap1.forEach(doc => studentMap.set(doc.id, { ...doc.data(), id: doc.id }));
                snap2.forEach(doc => studentMap.set(doc.id, { ...doc.data(), id: doc.id }));
                studentsList = Array.from(studentMap.values());

                // 3. Fetch Transactions
                const paymentsMap: Record<string, PaymentAllocations> = {};
                const providerPromises = paymentProviders.map(async (provider) => {
                    const qTx = query(
                        collection(db, `schools/${schoolID}/transactions/${provider}/records`),
                        where("status", "==", "completed")
                    );
                    const snapTx = await getDocs(qTx);
                    snapTx.forEach(doc => {
                        const data = doc.data();
                        const ref = data.accountReference;
                        if (!ref) return;
                        if (!paymentsMap[ref]) paymentsMap[ref] = { term1: 0, term2: 0, term3: 0 };
                        const amt = Number(data.amount) || 0;
                        const tKey = data.termKey || data.paymentForTerm || 'term1';
                        if (tKey === 'term1') paymentsMap[ref].term1 += amt;
                        else if (tKey === 'term2') paymentsMap[ref].term2 += amt;
                        else if (tKey === 'term3') paymentsMap[ref].term3 += amt;
                        else paymentsMap[ref].term1 += amt;
                    });
                });
                await Promise.all(providerPromises);

                // 4. Calculate for Each Student
                const records: StudentFinanceRecord[] = studentsList.map(student => {
                    const grade = student.admissionGrade || student.grade;
                    const feesObj = levelFees.find((l: any) => l.grade === grade) || {};
                    const hasAdmissionDate = !!student.admissionDate;

                    // Use centralized utility — handles categories, paidOnce, gov sponsorship, day school natively
                    const isOngoing = !!student.ongoing;
                    const isDaySchool = student.studentType === "Day School";
                    const totals = getApplicableTermFees(student.admissionDate, feesObj, terms, isOngoing, isDaySchool);

                    const regNo = student.registrationNumber;
                    const studentRef = `Fee-${regNo}`;
                    const paid = paymentsMap[studentRef] || { term1: 0, term2: 0, term3: 0 };
                    const broughtForward = Number(student.outstandingBalance || 0);
                    
                    const result = calculateFeeBalances(totals, paid, broughtForward);
                    
                    let status: 'paid' | 'overdue' | 'missing_data' = 'paid';
                    if (result.totalOutstanding > 0) status = 'overdue';
                    else if (!hasAdmissionDate) status = 'missing_data';

                    const totalFees = result.term1.required + result.term2.required + result.term3.required;
                    const totalPaid = result.term1.paid + result.term2.paid + result.term3.paid;

                    return {
                        id: student.id,
                        name: `${student.firstName} ${student.lastName}`,
                        regNo: regNo,
                        grade: grade || 'N/A',
                        feesRequired: totalFees,
                        amountPaid: totalPaid,
                        outstandingBalance: result.totalOutstanding,
                        hasMissingData: !hasAdmissionDate,
                        status: status
                    };
                });

                // Sort: Overdue first, then Missing Data, then Paid.
                records.sort((a, b) => {
                    const score = (s: string) => s === 'overdue' ? 3 : s === 'missing_data' ? 2 : 1;
                    return score(b.status) - score(a.status);
                });

                setStudents(records);
            } catch (error) {
                console.error("Error fetching class details:", error);
            } finally {
                setLoading(false);
            }
        };

        if (schoolID && className) {
            fetchClassData();
        }
    }, [schoolID, className, paymentProviders]);

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="flex items-center gap-4 border-b pb-4 border-border/40">
                <Button variant="outline" onClick={onBack} className="gap-2">
                    <ArrowLeft className="h-4 w-4" />
                    Back
                </Button>
                <div>
                    <h2 className="text-2xl font-bold tracking-tight">{className} Finance Details</h2>
                    <p className="text-muted-foreground">{students.length} students enrolled</p>
                </div>
            </div>

            <Card className="border-0 bg-transparent shadow-none">
                <CardContent className="p-0">
                    <div className="rounded-xl border border-border/50 bg-muted/10 backdrop-blur-sm overflow-hidden">
                        <div className="relative w-full overflow-auto">
                            <table className="w-full caption-bottom text-sm text-left">
                                <thead className="[&_tr]:border-b border-border/50 bg-muted/30">
                                    <tr className="border-b transition-colors data-[state=selected]:bg-muted">
                                        <th className="h-12 px-4 align-middle font-medium text-muted-foreground">Student Name</th>
                                        <th className="h-12 px-4 align-middle font-medium text-muted-foreground">Reg No</th>
                                        <th className="h-12 px-4 align-middle font-medium text-muted-foreground text-right">Required</th>
                                        <th className="h-12 px-4 align-middle font-medium text-muted-foreground text-right">Paid</th>
                                        <th className="h-12 px-4 align-middle font-medium text-muted-foreground text-right">Balance</th>
                                        <th className="h-12 px-4 align-middle font-medium text-muted-foreground text-center">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="[&_tr:last-child]:border-0">
                                    {loading ? (
                                        <tr>
                                            <td colSpan={6} className="h-32 text-center text-muted-foreground">
                                                <div className="flex flex-col items-center gap-2">
                                                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                                                    Loading class data...
                                                </div>
                                            </td>
                                        </tr>
                                    ) : students.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="h-32 text-center text-muted-foreground">
                                                No students found in this class.
                                            </td>
                                        </tr>
                                    ) : (
                                        students.map((student) => (
                                            <tr key={student.id} className="border-b border-border/40 transition-colors hover:bg-muted/30">
                                                <td className="p-4 align-middle font-medium text-base">{student.name}</td>
                                                <td className="p-4 align-middle text-muted-foreground">{student.regNo}</td>
                                                <td className="p-4 align-middle text-right font-mono">KES {student.feesRequired.toLocaleString()}</td>
                                                <td className="p-4 align-middle text-right font-mono">KES {student.amountPaid.toLocaleString()}</td>
                                                <td className="p-4 align-middle text-right font-mono font-bold">KES {student.outstandingBalance.toLocaleString()}</td>
                                                <td className="p-4 align-middle text-center">
                                                    {student.status === 'overdue' && (
                                                        <Badge variant="destructive">Overdue</Badge>
                                                    )}
                                                    {student.status === 'missing_data' && (
                                                        <Badge variant="outline" className="text-yellow-600 border-yellow-600 gap-1 bg-yellow-50 dark:bg-yellow-900/10">
                                                            <AlertTriangle className="h-3 w-3" /> Check Data
                                                        </Badge>
                                                    )}
                                                    {student.status === 'paid' && (
                                                        <Badge variant="outline" className="text-green-600 border-green-600 bg-green-50 dark:bg-green-900/10 gap-1">
                                                            <CheckCircle className="h-3 w-3" /> Paid
                                                        </Badge>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default ClassFinanceDetails;
